/**
 * 프로젝트 생성/수정 폼 컴포넌트
 */

import { useState, useEffect } from "react";
import { useKeyboardScroll } from "@/hooks/useKeyboardScroll";
import {
  FolderKanban,
  Wrench,
  MapPin,
  Calendar,
  User,
  UserCheck,
  FileText,
  Activity,
  Save,
} from "lucide-react";
import type { Project, ProjectStatus } from "@/types";
import { useToast } from "@/contexts/ToastContext";

export interface ProjectFormData {
  name: string;
  address: string;
  startDate: string;
  endDate: string;
  status: ProjectStatus;
  checkManager: string;
  witness: string;
  memo: string;
  type: string;
}

interface ProjectFormProps {
  initialData?: Partial<Project>;
  onSubmit: (data: ProjectFormData) => void;
  onCancel: () => void;
  isLoading?: boolean;
  mode: "create" | "edit";
}

const INSPECTION_TYPES = [
  { value: "PERFORMANCE_CHECK", label: "성능점검" },
  { value: "MAINTENANCE", label: "유지보수" },
];

const STATUS_OPTIONS: { value: ProjectStatus; label: string }[] = [
  { value: "IN_PROGRESS", label: "진행 중" },
  { value: "COMPLETED", label: "완료" },
  { value: "PENDING", label: "대기" },
];

export function ProjectForm({
  initialData,
  onSubmit,
  onCancel: _onCancel,
  isLoading,
  mode,
}: ProjectFormProps) {
  // onCancel은 헤더의 뒤로가기 버튼으로 대체됨 (props 호환성 유지)
  void _onCancel;
  const { showToast } = useToast();
  const scrollContainerRef = useKeyboardScroll<HTMLDivElement>();
  const [formData, setFormData] = useState<ProjectFormData>({
    name: "",
    address: "",
    startDate: "",
    endDate: "",
    status: "IN_PROGRESS",
    checkManager: "",
    witness: "",
    memo: "",
    type: "PERFORMANCE_CHECK",
  });

  useEffect(() => {
    if (initialData) {
      setFormData({
        name: initialData.name || "",
        address: initialData.address || "",
        startDate: initialData.startDate?.split("T")[0] || "",
        endDate: initialData.endDate?.split("T")[0] || "",
        status: initialData.status || "IN_PROGRESS",
        checkManager: initialData.checkManager || "",
        witness: initialData.witness || "",
        memo: initialData.memo || "",
        type: initialData.type || "PERFORMANCE_CHECK",
      });
    }
  }, [initialData]);

  const handleChange = (field: keyof ProjectFormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const validateForm = (): boolean => {
    const requiredFields = [
      { field: "name", label: "프로젝트명" },
      { field: "address", label: "현장주소" },
      { field: "checkManager", label: "점검자" },
      { field: "startDate", label: "점검시작일" },
      { field: "endDate", label: "종료일" },
    ];

    const emptyFields = requiredFields.filter(
      ({ field }) => !formData[field as keyof ProjectFormData]?.trim()
    );

    if (emptyFields.length > 0) {
      const fieldNames = emptyFields.map((f) => f.label).join(", ");
      showToast(`필수 입력값을 확인해주세요: ${fieldNames}`, "error");
      return false;
    }

    // 종료일이 시작일보다 앞에 있는지 검증
    if (formData.startDate && formData.endDate) {
      const start = new Date(formData.startDate);
      const end = new Date(formData.endDate);
      if (end < start) {
        showToast("종료일은 시작일보다 이후여야 합니다.", "error");
        return false;
      }
    }

    return true;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    // create 모드에서는 status 제외 (서버에서 기본값 설정)
    if (mode === "create") {
      const { status, ...createData } = formData;
      onSubmit(createData as typeof formData);
    } else {
      // edit 모드에서는 서버 UpdateProjectInput에 없는 type 필드 제외
      const { type, ...editData } = formData;
      onSubmit(editData as typeof formData);
    }
  };

  // 점검 유형 라벨 가져오기
  const getInspectionTypeLabel = (value: string) => {
    return INSPECTION_TYPES.find((t) => t.value === value)?.label || value;
  };

  return (
    <form onSubmit={handleSubmit} className="flex flex-col h-full">
      <div ref={scrollContainerRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-4">
        {/* 프로젝트명 */}
        <div>
          <label
            htmlFor="name"
            className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-1"
          >
            <FolderKanban className="w-4 h-4" />
            프로젝트명 <span className="text-red-500">*</span>
          </label>
          <input
            id="name"
            type="text"
            value={formData.name}
            onChange={(e) => handleChange("name", e.target.value)}
            className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
            placeholder="프로젝트명을 입력하세요"
          />
        </div>

        {/* 점검 유형 */}
        <div>
          <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-1">
            <Wrench className="w-4 h-4" />
            점검 유형
          </label>
          {mode === "create" ? (
            <div className="flex gap-2">
              {INSPECTION_TYPES.map((type) => (
                <button
                  key={type.value}
                  type="button"
                  onClick={() => handleChange("type", type.value)}
                  className={`flex-1 py-2.5 px-3 rounded-lg border text-sm font-medium transition-all ${
                    formData.type === type.value
                      ? "bg-blue-600 text-white border-blue-600"
                      : "bg-white text-gray-700 border-gray-300"
                  }`}
                >
                  {type.label}
                </button>
              ))}
            </div>
          ) : (
            <div className="w-full px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-lg text-gray-600">
              {getInspectionTypeLabel(formData.type)}
            </div>
          )}
        </div>

        {/* 현장주소 */}
        <div>
          <label
            htmlFor="address"
            className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-1"
          >
            <MapPin className="w-4 h-4" />
            현장주소 <span className="text-red-500">*</span>
          </label>
          <input
            id="address"
            type="text"
            value={formData.address}
            onChange={(e) => handleChange("address", e.target.value)}
            className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
            placeholder="현장 주소를 입력하세요"
          />
        </div>

        {/* 상태 (수정 모드에서만) */}
        {mode === "edit" && (
          <div>
            <label
              htmlFor="status"
              className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-1"
            >
              <Activity className="w-4 h-4" />
              상태
            </label>
            <select
              id="status"
              value={formData.status}
              onChange={(e) => handleChange("status", e.target.value)}
              className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
            >
              {STATUS_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* 시작일 / 종료일 */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label
              htmlFor="startDate"
              className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-1"
            >
              <Calendar className="w-4 h-4" />
              시작일 <span className="text-red-500">*</span>
            </label>
            <input
              id="startDate"
              type="date"
              value={formData.startDate}
              onChange={(e) => handleChange("startDate", e.target.value)}
              className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
            />
          </div>
          <div>
            <label
              htmlFor="endDate"
              className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-1"
            >
              <Calendar className="w-4 h-4" />
              종료일 <span className="text-red-500">*</span>
            </label>
            <input
              id="endDate"
              type="date"
              value={formData.endDate}
              onChange={(e) => handleChange("endDate", e.target.value)}
              className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
            />
          </div>
        </div>

        {/* 점검자 */}
        <div>
          <label
            htmlFor="checkManager"
            className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-1"
          >
            <User className="w-4 h-4" />
            점검자 <span className="text-red-500">*</span>
          </label>
          <textarea
            id="checkManager"
            value={formData.checkManager}
            onChange={(e) => handleChange("checkManager", e.target.value)}
            rows={2}
            placeholder="점검자 이름 (여러 명일 경우 줄바꿈)"
            className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none transition-all"
          />
        </div>

        {/* 입회자 */}
        <div>
          <label
            htmlFor="witness"
            className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-1"
          >
            <UserCheck className="w-4 h-4" />
            입회자
          </label>
          <input
            id="witness"
            type="text"
            value={formData.witness}
            onChange={(e) => handleChange("witness", e.target.value)}
            className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
            placeholder="입회자 이름"
          />
        </div>

        {/* 메모 */}
        <div>
          <label
            htmlFor="memo"
            className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-1"
          >
            <FileText className="w-4 h-4" />
            메모
          </label>
          <textarea
            id="memo"
            value={formData.memo}
            onChange={(e) => handleChange("memo", e.target.value)}
            rows={3}
            placeholder="프로젝트 관련 메모"
            className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none transition-all"
          />
        </div>

        {/* 저장 버튼 (스크롤 영역 내 인라인 배치) */}
        <div className="pt-2 pb-4">
          <button
            type="submit"
            disabled={isLoading}
            className="w-full flex items-center justify-center gap-2 py-3.5 px-4 bg-blue-600 text-white font-semibold rounded-xl hover:bg-blue-700 active:scale-[0.98] disabled:bg-gray-400 disabled:cursor-not-allowed transition-all shadow-lg shadow-blue-500/25"
          >
            <Save className="w-5 h-5" />
            {isLoading ? "처리 중..." : mode === "create" ? "프로젝트 생성" : "변경사항 저장"}
          </button>
        </div>
      </div>
    </form>
  );
}

export default ProjectForm;
